import React, { lazy } from 'react';
import { Routes, Route, Link, Navigate, useParams } from 'react-router-dom';

import ProtectedRoute from '../components/ProtectedRoute';
import PageAnalyticsTracker from './PageAnalyticsTracker';

const Dashboard = lazy(() => import('../pages/Dashboard'));
const DashboardSofiaEditionPage = lazy(() => import('../pages/DashboardSofiaEditionPage'));
const CampaignsP = lazy(() => import('../pages/CampaignsNewP'));
const CampaignCalendarV2Page = lazy(() => import('../pages/CampaignCalendarV2Page'));
const QaCycleV2Page = lazy(() => import('../pages/QaCycleV2Page'));
const DeviceWatchV2Page = lazy(() => import('../pages/DeviceWatchV2Page'));
const QaSimulationsV2Page = lazy(() => import('../pages/QaSimulationsV2Page'));
const DeviceObservationV2Page = lazy(() => import('../pages/DeviceObservationV2Page'));
const MailTrackingV2Page = lazy(() => import('../pages/MailTrackingV2Page'));
const DeviceNotificationsV2Page = lazy(() => import('../pages/DeviceNotificationsV2Page'));
const LocationHealthV2Page = lazy(() => import('../pages/LocationHealthV2Page'));
const PageAnalyticsV2Page = lazy(() => import('../pages/PageAnalyticsV2Page'));
const CampaignsDialog = lazy(() => import('../pages/CampaignsDialog'));
const AddCampaign = lazy(() => import('../pages/AddCampaign'));
const AddPlaylist = lazy(() => import('../components/AddPlaylist'));
const PlaylistsList = lazy(() => import('../pages/Playlists'));
const AddElements = lazy(() => import('../components/AddElements'));
const Devices = lazy(() => import('../pages/Devices'));
const Contacts = lazy(() => import('../pages/Contacts'));
const ContactsV2 = lazy(() => import('../pages/ContactsV2'));
const Login = lazy(() => import('../pages/Login'));
const Register = lazy(() => import('../pages/Register'));
const ForgotPassword = lazy(() => import('../pages/ForgotPassword'));
const ResetPassword = lazy(() => import('../pages/ResetPassword'));
const ImportPage = lazy(() => import('../pages/ImportPage'));
const UploadFile = lazy(() => import('../components/UploadFile'));
const FileList = lazy(() => import('../components/FileList'));
const Imports = lazy(() => import('../components/Imports'));
const PlaylistsDialog = lazy(() => import('../pages/PlaylistDialog'));
const CategoryManager = lazy(() => import('../components/CategoryManager'));
const PmiDevicesTable = lazy(() => import('../pages/PmiDevicesTable'));
const PmiDevicesTableP = lazy(() => import('../pages/pmiDevicesNew'));
const PmiDevicesV2 = lazy(() => import('../pages/PmiDevicesV2'));
const PmiLocationsV2 = lazy(() => import('../pages/PmiLocationsV2'));
const ListFilesV2 = lazy(() => import('../pages/ListFilesV2'));
const Locations = lazy(() => import('../pages/Locationss'));
const UserDevicesTable = lazy(() => import('../pages/UserDevicesTable'));
const CampaignPreviews = lazy(() => import('../pages/campaignsPreview'));
const SimulationResultsTable = lazy(() => import('../pages/SimulationResultsTable'));
const SimulationResultsPage = lazy(() => import('../pages/SimulationResultsPage'));
const CampaignDeviceAssignment = lazy(() => import('../components/CampaignDeviceAssignment'));
const CampaignDeviceLogs = lazy(() => import('../components/CampaignDeviceLogs'));
const Instructions = lazy(() => import('../pages/Instructions'));
const InstructionsNew = lazy(() => import('../components/instructionMarkdown'));
const DashboardStatus = lazy(() => import('../components/DashboardStatus'));
const ImportFileProcessor = lazy(() => import('../components/import/ImportFileProcessor'));
const ImportFieldMapper = lazy(() => import('../components/import/ImportFieldMapper'));
const Schedule = lazy(() => import('../pages/Schedule'));
const AddSchedule = lazy(() => import('../pages/AddSchedule'));
const PmiLocationsPage = lazy(() => import('../pages/PmiLocationsPage'));
const PmiMappingLogPage = lazy(() => import('../pages/PmiMappingLogPage'));
const TaTalAddressesPage = lazy(() => import('../pages/TaTalAddressesPage'));
const PmiDeviceLogsPage = lazy(() => import('../pages/PmiDeviceLogsPage'));
const NotifyFlowV2Page = lazy(() => import('../pages/NotifyFlowV2Page'));
const AuditV2Page = lazy(() => import('../pages/AuditV2Page'));
const CampaignEditLogsPage = lazy(() => import('../pages/CampaignEditLogsPage'));
const FileEditLogsPage = lazy(() => import('../pages/FileEditLogsPage'));
const ClientChangeLogPage = lazy(() => import('../pages/ClientChangeLogPage'));
const AccessControlPage = lazy(() => import('../pages/AccessControlPage'));
const AccessAuditLogsPage = lazy(() => import('../pages/AccessAuditLogsPage'));
const AccessAuditLogsV2Page = lazy(() => import('../pages/AccessAuditLogsV2Page'));
const RolesPermissionsPage = lazy(() => import('../pages/RolesPermissionsPage'));
const RolesPermissionsV2Page = lazy(() => import('../pages/RolesPermissionsV2Page'));
const ServerHealthPage = lazy(() => import('../pages/ServerHealthPage'));
const PmiRuntimeControlsPage = lazy(() => import('../pages/PmiRuntimeControlsPage'));
const DeviceConfigsPage = lazy(() => import('../pages/DeviceConfigsPage'));
const VersionManagementPage = lazy(() => import('../pages/VersionManagementPage'));
const ExecutiveSummaryPage = lazy(() => import('../pages/ExecutiveSummaryPage'));
const FeedbackBoardPage = lazy(() => import('../pages/FeedbackBoardPage'));
const FieldInstructionsPage = lazy(() => import('../pages/FieldInstructionsPage'));
const ProofOfPlayPage = lazy(() => import('../pages/ProofOfPlayPage'));
const ProductsPage = lazy(() => import('../pages/ProductsPage'));
const WeeklyReportPage = lazy(() => import('../pages/WeeklyReportPage'));
const InactiveEscalationPage = lazy(() => import('../pages/InactiveEscalationPage'));
const PrivacyConsentPage = lazy(() => import('../pages/PrivacyConsentPage'));
const CampaignAccessPage = lazy(() => import('../pages/CampaignAccessPage'));
const PmiRegistrationPage = lazy(() => import('../pages/PmiRegistrationPage'));
const PrivacyPolicyPage = lazy(() => import('../pages/PrivacyPolicyPage'));
const TermsOfUsePage = lazy(() => import('../pages/TermsOfUsePage'));
const AccountDeletionPage = lazy(() => import('../pages/AccountDeletionPage'));

function LandingPage({ token }) {
  const primaryAction = token
    ? { to: '/dashboard', label: 'Open Workspace' }
    : { to: '/login', label: 'Sign In' };

  return (
    <section className="landing-page">
      <div className="landing-page__hero">
        <div className="landing-page__brand">
          <img src="/onlabs-logo.png" alt="OnLabs" className="landing-page__logo" />
          <span className="landing-page__eyebrow">Workspace Platform</span>
        </div>
        <div className="landing-page__copy">
          <p className="landing-page__kicker">Designed for operational control</p>
          <h1>One workspace for campaigns, devices, proof of play and access governance.</h1>
          <p className="landing-page__lede">
            OnLabs brings campaign delivery, field visibility and operational audit trails into
            one controlled environment instead of spreading them across separate tools.
          </p>
          <div className="landing-page__actions">
            <Link to={primaryAction.to} className="landing-page__action landing-page__action--primary">
              {primaryAction.label}
            </Link>
            <Link to="/register" className="landing-page__action landing-page__action--secondary">
              Accept Invitation
            </Link>
          </div>
          <p className="landing-page__lede" style={{ marginTop: 18, fontSize: '0.95rem' }}>
            Legal: <Link to="/privacy-policy">Privacy Policy</Link> · <Link to="/terms-of-use">Terms of Use</Link> ·{' '}
            <Link to="/account-deletion">Account Deletion</Link>
          </p>
        </div>
      </div>

      <div className="landing-page__grid">
        <article className="landing-page__card">
          <span>Campaign Ops</span>
          <h2>Launch and monitor retail content from one place.</h2>
          <p>Campaign lists, device assignments, previews and history stay connected.</p>
        </article>
        <article className="landing-page__card">
          <span>Device Visibility</span>
          <h2>Work with live estate data instead of fragmented exports.</h2>
          <p>Locations, PMI devices, proof of play and troubleshooting are kept close to the workflow.</p>
        </article>
        <article className="landing-page__card">
          <span>Controlled Access</span>
          <h2>Keep every role, invitation and audit event traceable.</h2>
          <p>Access control and role permissions remain part of the product, not an afterthought.</p>
        </article>
      </div>
    </section>
  );
}

function LegacyPmiDeviceEditRedirect() {
  const { id } = useParams();
  const normalizedId = String(id || '').trim();

  if (!normalizedId) {
    return <Navigate to="/pmi-devicesP" replace />;
  }

  return <Navigate to={`/pmi-devicesP/edit/${encodeURIComponent(normalizedId)}`} replace />;
}

export default function AppRoutes({ token, saveToken }) {
  return (
    <>
    <PageAnalyticsTracker />
    <Routes>
      <Route path="/" element={<LandingPage token={token} />} />
      <Route path="/login" element={<Login setToken={saveToken} />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
      <Route path="/terms-of-use" element={<TermsOfUsePage />} />
      <Route path="/account-deletion" element={<AccountDeletionPage />} />
      <Route path="/privacy-consent/:token" element={<PrivacyConsentPage />} />
      <Route path="/campaign-access/:token" element={<CampaignAccessPage />} />
      <Route path="/pmi/registration" element={<PmiRegistrationPage />} />

      <Route
        path="/campaign-previews"
        element={
          <ProtectedRoute token={token}>
            <CampaignPreviews />
          </ProtectedRoute>
        }
      />
      <Route
        path="/simulation-results"
        element={
          <ProtectedRoute token={token}>
            <SimulationResultsTable />
          </ProtectedRoute>
        }
      />
      <Route
        path="/simulation-Page"
        element={
          <ProtectedRoute token={token}>
            <SimulationResultsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/devices"
        element={
          <ProtectedRoute token={token}>
            <Devices />
          </ProtectedRoute>
        }
      />
      <Route
        path="/pmi-devicesP"
        element={
          <ProtectedRoute token={token}>
            <PmiDevicesTableP />
          </ProtectedRoute>
        }
      />
      <Route
        path="/pmi-devicesP/edit/:id"
        element={
          <ProtectedRoute token={token}>
            <PmiDevicesTableP />
          </ProtectedRoute>
        }
      />
      <Route
        path="/pmi-devices-v2"
        element={
          <ProtectedRoute token={token}>
            <PmiDevicesV2 />
          </ProtectedRoute>
        }
      />
      <Route
        path="/pmi-locations-v2"
        element={
          <ProtectedRoute token={token}>
            <PmiLocationsV2 />
          </ProtectedRoute>
        }
      />
      <Route
        path="/list-files-v2"
        element={
          <ProtectedRoute token={token}>
            <ListFilesV2 />
          </ProtectedRoute>
        }
      />
      <Route
        path="/pmi-devices"
        element={
          <ProtectedRoute token={token}>
            <PmiDevicesTable />
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-devices"
        element={
          <ProtectedRoute token={token}>
            <UserDevicesTable />
          </ProtectedRoute>
        }
      />
      <Route path="/pmi-devices/edit/:id" element={<LegacyPmiDeviceEditRedirect />} />
      <Route path="/my-devices/edit/:id" element={<LegacyPmiDeviceEditRedirect />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute token={token}>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard-sofia-edition"
        element={
          <ProtectedRoute token={token}>
            <DashboardSofiaEditionPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard-sofia-edition-live"
        element={
          <ProtectedRoute token={token}>
            <DashboardSofiaEditionPage wallMode />
          </ProtectedRoute>
        }
      />
      <Route
        path="/upload-content"
        element={
          <ProtectedRoute token={token}>
            <UploadFile />
          </ProtectedRoute>
        }
      />
      <Route
        path="/list-files"
        element={
          <ProtectedRoute token={token}>
            <FileList />
          </ProtectedRoute>
        }
      />
      <Route
        path="/categories"
        element={
          <ProtectedRoute token={token}>
            <CategoryManager />
          </ProtectedRoute>
        }
      />
      <Route
        path="/schedule"
        element={
          <ProtectedRoute token={token}>
            <Schedule />
          </ProtectedRoute>
        }
      />
      <Route
        path="/addSchedule"
        element={
          <ProtectedRoute token={token}>
            <AddSchedule />
          </ProtectedRoute>
        }
      />
      <Route
        path="/manual-campaign-assignment"
        element={
          <ProtectedRoute token={token}>
            <CampaignDeviceAssignment />
          </ProtectedRoute>
        }
      />
      <Route
        path="/campaign-device-logs"
        element={
          <ProtectedRoute token={token}>
            <CampaignDeviceLogs />
          </ProtectedRoute>
        }
      />
      <Route
        path="/pmi-device-logs"
        element={
          <ProtectedRoute token={token}>
            <PmiDeviceLogsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/notify-flow-v2"
        element={
          <ProtectedRoute token={token}>
            <NotifyFlowV2Page />
          </ProtectedRoute>
        }
      />
      <Route
        path="/audit-v2"
        element={
          <ProtectedRoute token={token}>
            <AuditV2Page />
          </ProtectedRoute>
        }
      />
      <Route
        path="/campaign-change-logs"
        element={
          <ProtectedRoute token={token}>
            <CampaignEditLogsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/campaign-edit-logs"
        element={
          <ProtectedRoute token={token}>
            <CampaignEditLogsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/file-edit-logs"
        element={
          <ProtectedRoute token={token}>
            <FileEditLogsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/campaigns"
        element={
          <ProtectedRoute token={token}>
            <CampaignsP />
          </ProtectedRoute>
        }
      />
      <Route
        path="/campaignsP"
        element={
          <ProtectedRoute token={token}>
            <CampaignsP />
          </ProtectedRoute>
        }
      />
      <Route
        path="/campaignsDialog"
        element={
          <ProtectedRoute token={token}>
            <CampaignsDialog />
          </ProtectedRoute>
        }
      />
      <Route
        path="/addCampaign"
        element={
          <ProtectedRoute token={token}>
            <AddCampaign />
          </ProtectedRoute>
        }
      />
      <Route
        path="/playlistsDialog"
        element={
          <ProtectedRoute token={token}>
            <PlaylistsDialog />
          </ProtectedRoute>
        }
      />
      <Route
        path="/playlists"
        element={
          <ProtectedRoute token={token}>
            <PlaylistsList />
          </ProtectedRoute>
        }
      />
      <Route
        path="/add-elements"
        element={
          <ProtectedRoute token={token}>
            <AddElements onAdd={() => {}} />
          </ProtectedRoute>
        }
      />
      <Route
        path="/create-playlist"
        element={
          <ProtectedRoute token={token}>
            <AddPlaylist />
          </ProtectedRoute>
        }
      />
      <Route
        path="/contacts"
        element={
          <ProtectedRoute token={token}>
            <Contacts />
          </ProtectedRoute>
        }
      />
      <Route
        path="/contacts-v2"
        element={
          <ProtectedRoute token={token}>
            <ContactsV2 />
          </ProtectedRoute>
        }
      />
      <Route
        path="/instructions"
        element={
          <ProtectedRoute token={token}>
            <Instructions />
          </ProtectedRoute>
        }
      />
      <Route
        path="/InstructionsNew"
        element={
          <ProtectedRoute token={token}>
            <InstructionsNew />
          </ProtectedRoute>
        }
      />
      <Route
        path="/change-log"
        element={
          <ProtectedRoute token={token}>
            <ClientChangeLogPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/campaign-calendar-v2"
        element={
          <ProtectedRoute token={token}>
            <CampaignCalendarV2Page />
          </ProtectedRoute>
        }
      />
      <Route
        path="/qa-cycle-v2"
        element={
          <ProtectedRoute token={token}>
            <QaCycleV2Page />
          </ProtectedRoute>
        }
      />
      <Route
        path="/device-watch-v2"
        element={
          <ProtectedRoute token={token}>
            <DeviceWatchV2Page />
          </ProtectedRoute>
        }
      />
      <Route
        path="/qa-simulations-v2"
        element={
          <ProtectedRoute token={token}>
            <QaSimulationsV2Page />
          </ProtectedRoute>
        }
      />
      <Route
        path="/device-observation-v2"
        element={
          <ProtectedRoute token={token}>
            <DeviceObservationV2Page />
          </ProtectedRoute>
        }
      />
      <Route
        path="/mail-tracking-v2"
        element={
          <ProtectedRoute token={token}>
            <MailTrackingV2Page />
          </ProtectedRoute>
        }
      />
      <Route
        path="/device-notifications-v2"
        element={
          <ProtectedRoute token={token}>
            <DeviceNotificationsV2Page />
          </ProtectedRoute>
        }
      />
      <Route
        path="/location-health-v2"
        element={
          <ProtectedRoute token={token}>
            <LocationHealthV2Page />
          </ProtectedRoute>
        }
      />
      <Route
        path="/executive-summary"
        element={
          <ProtectedRoute token={token}>
            <ExecutiveSummaryPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/feedback-board"
        element={
          <ProtectedRoute token={token}>
            <FeedbackBoardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/field-instructions"
        element={
          <ProtectedRoute token={token}>
            <FieldInstructionsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/products"
        element={
          <ProtectedRoute token={token}>
            <ProductsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/access-control"
        element={
          <ProtectedRoute token={token}>
            <AccessControlPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/access-audit-logs"
        element={
          <ProtectedRoute token={token}>
            <AccessAuditLogsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/access-audit-logs-v2"
        element={
          <ProtectedRoute token={token}>
            <AccessAuditLogsV2Page />
          </ProtectedRoute>
        }
      />
      <Route
        path="/roles-permissions"
        element={
          <ProtectedRoute token={token}>
            <RolesPermissionsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/roles-permissions-v2"
        element={
          <ProtectedRoute token={token}>
            <RolesPermissionsV2Page />
          </ProtectedRoute>
        }
      />
      <Route
        path="/server-health"
        element={
          <ProtectedRoute token={token}>
            <ServerHealthPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/pmi-runtime-controls"
        element={
          <ProtectedRoute token={token}>
            <PmiRuntimeControlsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/device-configs"
        element={
          <ProtectedRoute token={token}>
            <DeviceConfigsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/device-configs-v2"
        element={
          <ProtectedRoute token={token}>
            <DeviceConfigsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/version-management"
        element={
          <ProtectedRoute token={token}>
            <VersionManagementPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/import"
        element={
          <ProtectedRoute token={token}>
            <ImportPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboardStatus"
        element={
          <ProtectedRoute token={token}>
            <DashboardStatus />
          </ProtectedRoute>
        }
      />
      <Route
        path="/weekly-report"
        element={
          <ProtectedRoute token={token}>
            <WeeklyReportPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/inactive-escalation"
        element={
          <ProtectedRoute token={token}>
            <InactiveEscalationPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/import/processor/:fileId"
        element={
          <ProtectedRoute token={token}>
            <ImportFileProcessor />
          </ProtectedRoute>
        }
      />
      <Route
        path="/import-field-mapper/:importTypeId"
        element={
          <ProtectedRoute token={token}>
            <ImportFieldMapper />
          </ProtectedRoute>
        }
      />
      <Route
        path="/imports"
        element={
          <ProtectedRoute token={token}>
            <Imports />
          </ProtectedRoute>
        }
      />
      <Route
        path="/locations"
        element={
          <ProtectedRoute token={token}>
            <Locations />
          </ProtectedRoute>
        }
      />
      <Route
        path="/pmi-locations"
        element={
          <ProtectedRoute token={token}>
            <PmiLocationsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/pmi-mapping-log"
        element={
          <ProtectedRoute token={token}>
            <PmiMappingLogPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/ta-tal-addresses"
        element={
          <ProtectedRoute token={token}>
            <TaTalAddressesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/proof-of-play"
        element={
          <ProtectedRoute token={token}>
            <ProofOfPlayPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/proof-of-play-live"
        element={
          <ProtectedRoute token={token}>
            <ProofOfPlayPage mapOnly />
          </ProtectedRoute>
        }
      />
      <Route
        path="/page-analytics-v2"
        element={
          <ProtectedRoute token={token}>
            <PageAnalyticsV2Page />
          </ProtectedRoute>
        }
      />
    </Routes>
    </>
  );
}
